import { Fragment, type CSSProperties, type ReactNode } from 'react';
import clsx from 'clsx';

/**
 * A manchete entrando PALAVRA A PALAVRA — em CSS puro.
 *
 * ## Por que não é um `Reveal` comum
 * A manchete é a primeira coisa que a pessoa lê, e entrar como um bloco só a
 * torna igual a qualquer cartão da página. Palavra a palavra dá a ela o peso
 * de abertura — é o gesto que separa "a página tem animação" de "a página tem
 * uma abertura".
 *
 * ## Por que não espera o viewport
 * Ela está ACIMA da dobra: um observador só atrasaria o que já está visível. A
 * animação roda no carregamento, e é `animation` (não `transition`), porque não
 * há mudança de estado — há uma entrada única.
 *
 * ## A manchete não espera um segundo render
 * Cada palavra é um `<span>` dentro do `<h1>` real, e quem anima é o CSS: não
 * há estado, não há efeito, não há "invisível até um `useEffect` rodar".
 * `animation-fill-mode: backwards` segura a palavra no quadro inicial durante
 * o atraso dela, sem precisar de `opacity: 0` no markup.
 *
 * O que isto NÃO resolve: a landing é renderizada no cliente, e o HTML servido
 * é uma casca de ~2 KB sem `<h1>` nenhum (conferido em produção). Sem
 * JavaScript não há manchete, e nenhuma escolha feita aqui muda isso. Quem
 * quiser a manchete no primeiro byte — LCP, indexação — precisa de
 * pré-renderização no build, que é decisão de infra da landing, não deste
 * componente.
 */

/** Onde a palavra vem, para o `@keyframes`. */
const ATRASO_POR_PALAVRA = 55;

export function MancheteAnimada({
  children,
  className,
}: {
  /** Texto e destaques. Strings são fatiadas em palavras; nós ficam inteiros. */
  children: ReactNode;
  className?: string;
}): JSX.Element {
  let indice = 0;

  const animar = (no: ReactNode, chave: string | number): ReactNode => {
    if (typeof no === 'string') {
      /*
        Fatia mantendo os espaços: `split(/(\s+)/)` devolve separadores, e
        assim a quebra de linha continua sendo a do navegador. Um `join(' ')`
        com `inline-block` por palavra quebraria o `text-balance` do título.
      */
      return no.split(/(\s+)/).map((pedaco, i) => {
        if (!pedaco.trim())
          return <Fragment key={`${chave}-e${String(i)}`}>{pedaco}</Fragment>;
        const estilo = {
          '--atraso-palavra': `${String(indice++ * ATRASO_POR_PALAVRA)}ms`,
        } as CSSProperties;
        return (
          <span key={`${chave}-p${String(i)}`} className="palavra-entra" style={estilo}>
            {pedaco}
          </span>
        );
      });
    }
    return no;
  };

  const filhos = Array.isArray(children) ? children : [children];

  return (
    <h1
      className={clsx(
        /*
          PESO 500, não 300.

          A manchete estava no peso mais leve que a fonte tem. Título grande
          em Light lê como lavado — foi o que o dono chamou de "sem vida", e a
          medição confirmou: a referência que ele apontou usa 600 na manchete
          e 300 no corpo. Aqui os dois eram 300, então não havia contraste
          nenhum entre o que grita e o que explica.

          O tracking fecha junto: em corpo de texto -0.025em já basta, mas a
          3.4rem o espaço entre letras cresce com o tamanho e o título
          "abre". -0.035em devolve a densidade que faz um display parecer
          desenhado, e não apenas ampliado.
        */
        'text-auth-texto text-4xl leading-[1.06] font-medium tracking-[-0.035em] text-balance sm:text-5xl lg:text-[3.4rem]',
        className,
      )}
    >
      {filhos.map((filho, i) => (
        <Fragment key={i}>{animar(filho, i)}</Fragment>
      ))}
    </h1>
  );
}
